import { ApprovalsService, CancelablePromise } from 'api-clients/monolith';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Decision } from './DecisionFooter';
import { ApprovalRequest } from './types';

export interface BulkOutcome {
  decision: Decision;
  requested: number;
  succeeded: number;
}

export interface BulkRun {
  decision: Decision;
  count: number;
}

/**
 * Records one decision against each of several openings (AXHE-3934).
 *
 * ## One request per row, and per row one level
 *
 * There is no bulk endpoint, and the shape of the one that exists rules a naive
 * loop out of being an implementation detail: `POST .../requests/:id/decisions`
 * takes a single `step_id`, refuses anyone who is not the resolved approver on it,
 * and is the thing that advances the ladder. So this is N requests, each deciding
 * the level in front of the viewer on that row — the same level the row's own
 * Approve button would have decided.
 *
 * ## Partial success is the normal outcome, not the error case
 *
 * Any one of the N can be refused on its own merits: a colleague decided that
 * level a second earlier, or the rule behind it was deleted while the page was
 * open. `allSettled`, not `all` — the first rejection must not abandon requests
 * that have already committed, and cannot un-commit the ones that landed.
 *
 * What comes back is therefore a count rather than a verdict. The caller
 * re-requests the page, so which rows moved is read from the table; the count is
 * what says whether to look.
 *
 * ## No retry of the failures
 *
 * Deliberately. Every reason a decision is refused here is one that re-posting
 * cannot fix — the level is closed, or it was never this person's. A retry would
 * be a second round of 4xxes dressed as a recovery.
 */
export const useBulkDecision = (options: { onSettled: () => void }) => {
  // Which decision the run in flight is recording, and against how many rows.
  // Both are frozen at the press rather than read from the live selection, so a
  // tick landing mid-run cannot change what the bar claims is being decided.
  // The decision rides along so the bar can put its progress on the button that
  // is actually running.
  const [running, setRunning] = useState<BulkRun | null>(null);
  const [outcome, setOutcome] = useState<BulkOutcome | null>(null);
  // Held so unmounting mid-run aborts the rest rather than resolving into a
  // component that is gone. `useApiService` does this for its own request; these
  // are issued directly, so it is done here.
  const inFlight = useRef<Array<CancelablePromise<unknown>>>([]);
  const mounted = useRef(true);
  // Read through a ref so `run` keeps a stable identity whatever the caller does
  // with its options object.
  const onSettled = useRef(options.onSettled);
  onSettled.current = options.onSettled;

  useEffect(
    () => () => {
      mounted.current = false;
      inFlight.current.forEach(promise => promise.cancel());
      inFlight.current = [];
    },
    [],
  );

  const run = useCallback(
    (rows: ApprovalRequest[], decision: Decision, comment?: string) => {
      const decidable = rows.filter(row => row.actionableStepIds.length > 0);
      if (decidable.length === 0) return;

      setRunning({ decision, count: decidable.length });
      setOutcome(null);

      const requests = decidable.map(row =>
        ApprovalsService.postInternalApiApprovalsRequestsDecisions(row.id, {
          step_id: row.actionableStepIds[0],
          decision,
          comment,
        }),
      );
      inFlight.current = requests as Array<CancelablePromise<unknown>>;

      Promise.allSettled(requests).then(results => {
        inFlight.current = [];
        // Cancelling on unmount rejects each promise with a `CancelError`, so
        // `allSettled` still resolves — into a component that is gone.
        if (!mounted.current) return;

        setRunning(null);
        setOutcome({
          decision,
          requested: results.length,
          succeeded: results.filter(result => result.status === 'fulfilled')
            .length,
        });
        // After the counts, so the re-render that swaps the bar for the summary
        // carries both at once.
        onSettled.current();
      });
    },
    [],
  );

  return {
    run,
    /** The decision in flight and the rows it covers, or null when none is. */
    running,
    outcome,
    clearOutcome: () => setOutcome(null),
  };
};
